import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Phone, Hash, User, MapPin, Loader2, AlertCircle, Eye } from 'lucide-react';
import Swal from 'sweetalert2';

function InspectionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState(null);
  const [inspectionData, setInspectionData] = useState({
    phone: "",
    tin: "",
    name: "",
    city: "",
    woreda: "",
  });

  // API instance with proper configuration
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });

  // Add auth token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInspectionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle inspection button click - fetch and navigate to details
  const HandleInspection = async (itemId) => {
    setLoading(true);
    
    try {
      const response = await api.get(`/inspection/${itemId}`);
      console.log('Inspection data received:', response.data);
      
      if (response.data && response.data.success !== false) {
        navigate('/admin/inspectionDetail', { 
          state: { 
            data: response.data,
            traderId: itemId 
          } 
        });
      } else {
        throw new Error(response.data?.message || 'Failed to fetch inspection details');
      }
    } catch (err) {
      console.error('Error fetching inspection details:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to fetch inspection details. Please try again.',
        timer: 3000,
        showConfirmButton: false
      });
      setError("Failed to fetch inspection details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search submission
  const HandleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      const searchParams = {};
      if (inspectionData.phone) searchParams.phone = inspectionData.phone;
      if (inspectionData.tin) searchParams.tin = inspectionData.tin;
      if (inspectionData.name) searchParams.name = inspectionData.name;
      if (inspectionData.city) searchParams.city = inspectionData.city;
      if (inspectionData.woreda) searchParams.woreda = inspectionData.woreda;

      if (Object.keys(searchParams).length === 0) {
        setError("Please enter at least one search criterion");
        setLoading(false);
        return;
      }

      const response = await api.post('/searchtraders', searchParams);
      
      if (response.data) {
        let results = [];
        if (response.data.data && Array.isArray(response.data.data)) {
          results = response.data.data;
        } else if (response.data.traders && Array.isArray(response.data.traders)) {
          results = response.data.traders;
        } else if (Array.isArray(response.data)) {
          results = response.data;
        } else if (response.data.data && !Array.isArray(response.data.data)) {
          results = [response.data.data];
        }
        
        setSearchResult(results);
        
        if (results.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'No Results',
            text: 'No traders found matching your search criteria',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `Found ${results.length} trader(s)`,
            timer: 2000,
            showConfirmButton: false
          });
        }
      }
    } catch (err) {
      console.error("Search error:", err);
      setError(err.response?.data?.message || "Failed to search traders. Please try again.");
      Swal.fire({
        icon: 'error',
        title: 'Search Failed',
        text: err.response?.data?.message || 'Failed to search traders',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset search
  const handleReset = () => {
    setInspectionData({
      phone: "",
      tin: "",
      name: "",
      city: "",
      woreda: "",
    });
    setSearchResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-indigo-700 px-4 sm:px-6 py-5 sm:py-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Inspection Search</h1>
              <p className="text-indigo-100 text-sm sm:text-base">Search and manage trader inspections</p>
            </div>
          </div>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Search size={20} className="text-indigo-700" />
              Search Traders
            </h2>
          </div>
          
          <form onSubmit={HandleSearch} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Phone size={14} />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={inspectionData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* TIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Hash size={14} />
                  TIN Number
                </label>
                <input
                  type="text"
                  name="tin"
                  value={inspectionData.tin}
                  onChange={handleInputChange}
                  placeholder="Enter TIN number"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Hash size={14} />
                  ID
                </label>
                <input
                  type="text"
                  name="tin"
                  value={inspectionData.id}
                  onChange={handleInputChange}
                  placeholder="Enter TIN number"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <User size={14} />
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={inspectionData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin size={14} />
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={inspectionData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Woreda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin size={14} />
                  Woreda/Subcity
                </label>
                <input
                  type="text"
                  name="woreda"
                  value={inspectionData.woreda}
                  onChange={handleInputChange}
                  placeholder="Enter woreda or subcity"
                  className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-2 items-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search size={18} />
                      Search
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResult && !error && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-4 bg-indigo-50 border-b border-indigo-100">
              <h2 className="text-lg font-bold text-gray-800">Search Results</h2>
              <p className="text-sm text-gray-600 mt-1">
                Found {searchResult.length} trader(s)
              </p>
            </div>
            
            {searchResult.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TIN</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {searchResult.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                          {item.name || item.fullname || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                          {item.tin || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.phone || item.phone_number || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.business_name || item.company_name || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            item.status === 'active' ? 'bg-green-100 text-green-800' :
                            item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            item.status === 'inactive' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => HandleInspection(item.id)}
                            disabled={loading}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Eye size={16} />
                            Inspect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-gray-500">No traders found</p>
              </div>
            )}
          </div>
        )}

        {/* No Results State */}
        {!searchResult && !error && !loading && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8 text-center">
              <Search size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Enter search criteria above to find traders</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InspectionPage;